import { useState } from 'react'
import { ChevronLeft, ChevronRight, Quote, Star } from 'lucide-react'
import type { Testimonial } from '../types'

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0)

  const testimonials: Testimonial[] = [
    {
      id: 'sarah',
      name: 'Sarah Martinez',
      company: 'TechFlow Solutions',
      result: '300% increase in organic traffic',
      quote: "Clybit's SEO strategy completely transformed our online presence. Within 6 months, we saw a 300% increase in organic traffic and our leads doubled. Their data-driven approach and attention to detail is unmatched.",
      avatar: 'SM'
    },
    {
      id: 'michael',
      name: 'Michael Chen',
      company: 'GreenTech Innovations',
      result: '450% ROI on PPC campaigns',
      quote: "The PPC campaigns Clybit created for us delivered incredible results. We achieved a 450% ROI within the first quarter, and their ongoing optimization continues to improve our performance month after month.",
      avatar: 'MC'
    },
    {
      id: 'emma',
      name: 'Emma Thompson',
      company: 'Fashion Forward',
      result: '500% social media engagement boost',
      quote: "Our social media presence was practically non-existent before working with Clybit. They helped us build a community of over 50K engaged followers and increased our social media engagement by 500%. Amazing results!",
      avatar: 'ET'
    },
    {
      id: 'david',
      name: 'David Rodriguez',
      company: 'Elite Fitness Co.',
      result: '2x conversion rate improvement',
      quote: "Clybit redesigned our website and optimized our conversion funnel, resulting in a 2x improvement in our conversion rate. Their strategic approach to web design and UX is exactly what our business needed.",
      avatar: 'DR'
    }
  ]

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length)
  }

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length)
  }

  const goToTestimonial = (index: number) => {
    setCurrentIndex(index)
  }

  const currentTestimonial = testimonials[currentIndex]

  return (
    <section id="testimonials" className="section-spacing bg-white">
      <div className="max-w-7xl mx-auto container-padding">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Client Success Stories
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Don't just take our word for it. See how we've helped businesses like yours 
            achieve remarkable growth and success.
          </p>
        </div>

        {/* Main Testimonial */}
        <div className="relative bg-primary-50 rounded-2xl p-8 lg:p-12 mb-12">
          <div className="max-w-4xl mx-auto">
            {/* Quote Icon */}
            <div className="flex justify-center mb-8">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center">
                <Quote size={32} className="text-white" />
              </div>
            </div>

            {/* Testimonial Content */}
            <div className="text-center">
              <blockquote className="text-xl lg:text-2xl text-gray-900 font-medium leading-relaxed mb-8">
                "{currentTestimonial.quote}"
              </blockquote>

              {/* Client Info */}
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center font-bold text-lg mb-4">
                  {currentTestimonial.avatar}
                </div>
                <div className="font-semibold text-gray-900 text-lg">
                  {currentTestimonial.name}
                </div>
                <div className="text-gray-600 mb-2">
                  {currentTestimonial.company}
                </div>
                <div className="bg-secondary text-white px-4 py-2 rounded-full text-sm font-semibold">
                  {currentTestimonial.result}
                </div>
              </div>
            </div>

            {/* Navigation Arrows */}
            <div className="absolute top-1/2 -translate-y-1/2 left-4 lg:left-8">
              <button
                onClick={prevTestimonial}
                className="w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors duration-200"
              >
                <ChevronLeft size={20} className="text-gray-600" />
              </button>
            </div>
            <div className="absolute top-1/2 -translate-y-1/2 right-4 lg:right-8">
              <button
                onClick={nextTestimonial}
                className="w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors duration-200"
              >
                <ChevronRight size={20} className="text-gray-600" />
              </button>
            </div>
          </div>
        </div>

        {/* Testimonial Indicators */}
        <div className="flex justify-center gap-3 mb-12">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => goToTestimonial(index)}
              className={`w-3 h-3 rounded-full transition-colors duration-200 ${
                index === currentIndex ? 'bg-primary' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>

        {/* All Testimonials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {testimonials.map((testimonial, index) => (
            <div
              key={testimonial.id}
              className={`bg-white border rounded-xl p-6 cursor-pointer transition-all duration-200 ${
                index === currentIndex 
                  ? 'border-primary shadow-lg transform scale-105' 
                  : 'border-gray-200 hover:border-primary-200 hover:shadow-md'
              }`}
              onClick={() => goToTestimonial(index)}
            >
              {/* Stars */}
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} className="text-yellow-400 fill-current" />
                ))}
              </div>

              {/* Mini Quote */}
              <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                "{testimonial.quote.substring(0, 80)}..."
              </p>

              {/* Client */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center font-semibold text-sm">
                  {testimonial.avatar}
                </div>
                <div>
                  <div className="font-semibold text-gray-900 text-sm">
                    {testimonial.name}
                  </div>
                  <div className="text-gray-500 text-xs">
                    {testimonial.company}
                  </div>
                </div>
              </div>

              {/* Result Badge */}
              <div className="mt-4 bg-secondary-50 text-secondary text-xs font-semibold px-3 py-1 rounded-full">
                {testimonial.result}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default Testimonials